<template>
  <div>
    <div class="blocks">
      <div class="row pt-3 l-m-b-20 d-flex align-items-center">
        <div class="col-2">
          <h1>Mover Totals</h1>
        </div>
        <div class="col-6">
          <div class="input-group search">
            <button type="button" class="btn input-group-btn" @click="clearSearch">
              <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
            </button>
            <input class="form-control" type="text" v-model="q" @keyup="searchText($event)"
                   placeholder="Search"
            />
          </div>
        </div>
        <div class="col-3 text-right mobile-hidden">
          <button class="btn btn-primary btn-schedule text-uppercase" @click="markAllAsPaid">
            Mark all as paid
          </button>
        </div>
        <div class="col-1 text-right mobile-hidden">
          <button class="btn btn-primary btn-schedule text-uppercase ld-ext-right" :class="exporting ? 'running' : ''" @click="exportData" :disabled="exporting">
            Export
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <div class="row mb-2">
        <div class="col-11 filters-field">
          <span>Filters:</span>
          <span v-if="filtersLoaded">
            <span v-if="filters.move_date" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('move_date')" />
              Move Date: {{ filters.move_date }}</span>
            <span v-if="filters.carrier.length">
              <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
                Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
            </span>
            <span v-if="filters.movers.length">
              <span v-for="mover in filters.movers" :key="mover" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('movers', mover)" />
                Mover: {{ movers.find(el => el.id === mover).name }}</span>
            </span>
            <span v-if="filters.is_paid" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="removeFilter('is_paid')" />
              Is Paid: {{ filters.is_paid ? 'Yes' : 'No' }}</span>
          </span>
          <button type="button" class="btn btn-secondary add-filters" @click="showFilters = true">
            <i class="fa fa-plus filter-icon" />
          </button>
        </div>
        <div class="col-1 d-flex justify-content-end">
          <div class="dropdown">
            <button class="btn btn-secondary sort-button sort-button-selected" id="dropdownMenuButton"
                    data-toggle="dropdown" aria-haspopup="true"
                    aria-expanded="false"
            >
              <i v-if="selectedKey.length" class="fa pr-2" :class="[ascOrDesc === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc']" />
              <span v-if="selectedKey === 'move_date'">
                Move date
              </span>
              <span v-else>
                Sort by
              </span>
              <i class="fa fa-caret-down caret" />
            </button>
            <div class="dropdown-menu sort-dropdown dropdown-menu-right" aria-labelledby="dropdownMenuButton">
              <button class="dropdown-item sort-dropdown-item nav-link btn" @click.stop="sortBy('move_date')">
                <span :class="[selectedKey === 'move_date' ? 'selected' : '']">
                  Move date
                </span>
                <span>
                  <i v-if="selectedKey === 'move_date'" class="fa" :class="[ascOrDesc === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc']" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="loading">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else-if="!moverTotals.length">
      <span>There are no results.</span>
    </template>
    <template v-else>
      <MoverTotalsHeader />

      <MoverTotalsStatistics class="blocks" :meta="meta" :sums="sums" />

      <div class="blocks">
        <MoverTotalsBlock v-for="total in moverTotals" :key="`${total.job_id}${total.mover_id}`" :total="total" @update="filter" />
      </div>

      <div class="row blocks">
        <div class="col-sm-6 mb-1 text-black-50 pl-0"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1 pr-0" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Filters -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showFilters" @click.self="showFilters = false">
        <div v-if="showFilters" class="filters-side-modal flex flex-column">
          <div @click="showFilters = !showFilters" class="pointer">
            <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Add Filter</h1>
          </div>
          <div>
            <p>
              Filters are applied automatically. You can remove them one by one with minus button or with clear filters button.
            </p>
          </div>
          <div class="l-bg-white">
            <div class="row">
              <div class="col">
                <div class="row filters">
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Move date</label>
                      <flat-pickr v-model="filters.move_date"
                                  :config="config"
                                  class="simple-select-input"
                                  placeholder="Select Move Date"
                                  name="date"
                                  @on-close="onMoveDateChange"
                      />
                      <span class="position-absolute clear-date" @click="clearMoveDateAndSearch"
                            v-if="filters.move_date"
                      >
                        <span class="mod-icon-clear" />
                        <span aria-hidden="true" class="sr-only">Clear</span>
                      </span>
                      <span class="position-absolute clear-date clear-date-transparent"
                            v-else
                      ><span class="mod-icon-rates-dark" /></span>
                    </div>
                  </div>
                  <div class="col-12" v-if="carriers && carriers.length">
                    <div class="select-wrap">
                      <label>Carriers</label>
                      <key-multiselect v-model="filters.carrier"
                                       :options="carriers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Movers</label>
                      <key-multiselect v-model="filters.movers"
                                       :options="movers"
                                       :multiple="true"
                                       :clear-on-select="false"
                                       :close-on-select="false"
                                       :hide-selected="true"
                                       :preserve-search="false"
                                       label="name"
                                       track-by="id"
                                       @input="search"
                      >
                        <template slot="tag" slot-scope="props">
                          <div class="multiselect__tag">
                            <span>{{ props.option.name }}</span>
                            <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                          </div>
                        </template>
                      </key-multiselect>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="select-wrap">
                      <label>Status</label>
                      <simple-select v-model="filters.is_paid"
                                     :options="booleanOptions"
                                     placeholder="Select"
                                     @input="search"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-auto">
            <div class="col">
              <button type="button" class="btn btn-not-active text-dark w-100" @click="clearFilters"
                      v-if="!clearFiltersButtonShouldBeDisabled"
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import toastr from 'toastr';
import { mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import axios from 'axios';
import moment from 'moment';
import Pagination from '../Pagination';
import MoverTotalsHeader from './MoverTotalsHeader';
import MoverTotalsStatistics from './MoverTotalsStatistics';
import MoverTotalsBlock from './MoverTotalsBlock';

export default {
  name: 'MoverTotals',
  components: {
    MoverTotalsBlock,
    Pagination,
    flatPickr,
    MoverTotalsHeader,
    MoverTotalsStatistics,
  },
  data: () => ({
    averages: [],
    config: {
      dateFormat: 'm/j/Y',
      mode: 'range',
    },
    exporting: false,
    filters: {
      carrier: [],
      movers: [],
      is_paid: '',
      moveDateEnd: '',
      moveDateStart: '',
      move_date: '',
      sortKey: '',
      sortOrders: '',
    },
    selectedKey: '',
    meta: [],
    page: 1,
    q: '',
    cancelToken: '',
    source: '',
    urlQuery: '',
    inProgress: false,
    showFilters: false,
    loading: false,
    booleanOptions: [
      {
        id: 1,
        name: 'Paid',
      },
      {
        id: 0,
        name: 'Unpaid',
      },
    ],
    sums: [],
    movers: [],
    moverTotals: [],
  }),
  computed: {
    ...mapState({
      filtersLoaded: state => state.guard.filtersLoaded,
      carriers: state => state.carriers.carriers.data,
    }),
    ascOrDesc() {
      return this.filters.sortOrders === 'asc' ? 'asc' : 'desc';
    },
    clearFiltersButtonShouldBeDisabled() {
      return (
        !this.filters.move_date &&
        !this.filters.carrier.length &&
        !this.filters.movers.length &&
        this.filters.is_paid === ''
      );
    },
  },
  created() {
    this.cancelToken = axios.CancelToken;
    this.source = this.cancelToken.source();
  },
  mounted() {
    this.getMovers();
    if (Object.keys(this.$route.query).length !== 0) {
      this.setFilters(this.$route.query);
    }
    this.filter();
  },
  methods: {
    getMovers() {
      axios.get('/movers?paginated=false').then(res => {
        res.data.data.forEach(mover => {
          mover.name = `${mover.first_name} ${mover.last_name}`;
        });
        this.movers = res.data.data;
      });
    },
    markAllAsPaid() {
      axios.post(`/movers/totals/paid?${this.urlQuery}`).then(() => {
        this.filter();
      });
    },
    exportData() {
      this.exporting = true;
      axios({
        url: `/movers/totals/export?${this.urlQuery}`,
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute(
            'download',
            `mover-earnings-${moment().format('MM-DD-YYYY-hh-mm')}.pdf`,
          );
          document.body.appendChild(fileLink);

          fileLink.click();
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    setFilters(query) {
      this.filters = Object.assign(
        {
          carrier: query.carrier_id ? query.carrier_id.split(',').map(i => Number(i)) : [],
          movers: query.user_id ? query.user_id.split(',').map(i => Number(i)) : [],
          is_paid: query.is_paid ? parseInt(query.is_paid, 10) : '',
          moveDateStart: query.date_from ? query.date_from.toString() : '',
          moveDateEnd: query.date_to ? query.date_to.toString() : '',
          move_date:
            query.date_from && query.date_to
              ? `${query.date_from.toString()} to ${query.date_to.toString()}`
              : '',
          sortKey: query.sort,
          sortOrders: query.order,
        },
        {},
      );
      this.q = query.q.toString();
      this.page = query.page;
      this.selectedKey = this.filters.sortKey;
    },
    changePage(pageNum) {
      this.page = pageNum;
      this.filter();
    },
    getMoverTotalsFiltered(query) {
      this.loading = true;
      this.inProgress = true;
      axios
        .get(`/movers/totals?${query}`, { cancelToken: this.source.token })
        .then(res => {
          this.moverTotals = res.data.data;
          this.sums = res.data.sums;
          this.meta = res.data.meta;
        })
        .finally(() => {
          this.loading = false;
          this.inProgress = false;
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            toastr.error(err.message);
            this.loading = false;
          }
        });
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      const query =
        `page=${this.page}&date_from=${this.filters.moveDateStart}` +
        `&carrier_id=${this.filters.carrier}` +
        `&user_id=${this.filters.movers}` +
        `&is_paid=${this.filters.is_paid}` +
        `&date_to=${this.filters.moveDateEnd}&q=${encodeURIComponent(this.q)}` +
        `&sort=${this.filters.sortKey}&order=${this.filters.sortOrders}`;
      this.urlQuery = query;
      if (!this.inProgress) {
        this.getMoverTotalsFiltered(query);
        if (this.$_checkProperties(this.filters)) {
          this.$router.push(`/reports/mover-totals?${query}`);
        } else {
          this.$router.push(`/reports/mover-totals`);
        }
      } else {
        // If there is a request in progress, cancel it, and try again with other request
        this.source.cancel('Operation canceled by the user.');
        // Set axios cancel token and source to new values, so you can repeat request
        this.cancelToken = axios.CancelToken;
        this.source = this.cancelToken.source();
        this.inProgress = false;
        this.getMoverTotalsFiltered(query);
        if (this.$_checkProperties(this.filters)) {
          this.$router.push(`/reports/mover-totals?${query}`);
        } else {
          this.$router.push(`/reports/mover-totals`);
        }
      }
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          movers: [],
          is_paid: '',
          move_date: '',
          moveDateStart: '',
          moveDateEnd: '',
          sortKey: '',
          sortOrders: '',
        },
        {},
      );
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    clearMoveDateAndSearch() {
      this.filters.move_date = '';
      this.filters.moveDateStart = '';
      this.filters.moveDateEnd = '';
      this.page = 1;
      this.filter();
    },
    onMoveDateChange(selectedDates) {
      this.filters.moveDateStart = selectedDates[0]
        ? selectedDates[0].toLocaleDateString('en-US')
        : '';
      this.filters.moveDateEnd = selectedDates[1]
        ? selectedDates[1].toLocaleDateString('en-US')
        : '';
      this.search();
    },
    sortBy(key) {
      this.selectedKey = key;
      this.$set(this.filters, 'sortKey', key);
      this.$set(this.filters, 'sortOrders', this.filters.sortOrders === 'desc' ? 'asc' : 'desc');
      this.filter();
    },
    removeFilter(model, id) {
      if (id) {
        this.filters[model] = this.filters[model].filter(element => element !== id);
      } else {
        this.filters[model] = '';
      }
      if (model === 'move_date') {
        this.filters.move_date = '';
        this.filters.moveDateStart = '';
        this.filters.moveDateEnd = '';
      }
      this.search();
    },
  },
};
</script>
<style lang="scss" scoped>
.totals-wrapper {
  height: calc(80vh - 172px);
  overflow: auto;
}
.input-group {
  width: 35%;
}
</style>



// WEBPACK FOOTER //
// src/components/reports/MoverTotals.vue